/**
 * Applicant-facing copy for a locked / view-only step.
 *
 * The portal NEVER derives lock state — Globus' step-access snapshot is the
 * sole access authority (see applyStepAccess in steps.ts). This module only
 * turns the snapshot's `lock_reason_code` + `unlock_conditions` into friendly
 * text when no staff-authored `banner_message` exists. Staff-authored
 * messages always win and render verbatim.
 */

export const UPSTREAM_STEP_INCOMPLETE = 'UPSTREAM_STEP_INCOMPLETE';

/** Resolves a step key to its configured applicant-facing title. */
export type StepTitleResolver = (stepKey: string) => string | undefined;

/**
 * Builds a title resolver over the configured (UNFILTERED) step list. Facts
 * may carry the unversioned key (`step.personal_information.status`) while
 * the workflow key is versioned (`personal_information_v1_0`), so an exact
 * match is tried first, then `<key>_v<major>[_<minor>...]`.
 */
export function makeStepTitleResolver(
  steps: ReadonlyArray<{ stepKey: string; title?: string }>,
): StepTitleResolver {
  return (stepKey) => {
    const exact = steps.find((s) => s.stepKey === stepKey);
    if (exact) return exact.title;
    const versioned = steps.find(
      (s) =>
        s.stepKey.startsWith(`${stepKey}_v`) &&
        /^_v\d+(_\d+)*$/.test(s.stepKey.slice(stepKey.length)),
    );
    return versioned?.title;
  };
}

/**
 * Fallback banner copy keyed loosely on the lock reason code so upstream
 * code-spelling drift (e.g. COMPLIANCE_BLOCK vs COMPLIANCE_HOLD) still lands
 * on the right copy.
 */
export function defaultLockMessage(reason: string | undefined, viewOnly: boolean): string {
  const code = (reason ?? '').toUpperCase();
  if (code === UPSTREAM_STEP_INCOMPLETE || (code.includes('UPSTREAM') && code.includes('INCOMPLETE')))
    return 'This step unlocks once an earlier step has been approved by our team.';
  if (code.includes('SUBMITTED') || code.includes('COMPLETED'))
    return "You've already submitted this step, so it can no longer be edited. Contact your program team if something needs to change.";
  if (code.includes('COMPLIANCE'))
    return 'This section is locked while our team reviews a requirement on your application. No action is needed from you right now — contact your program team if you have questions.';
  if (code.includes('SEVIS') || code.includes('TRACK'))
    return 'This section unlocks once the earlier steps in your visa process are finished.';
  if (code.includes('TIME') || code.includes('WINDOW'))
    return 'This section is temporarily locked and will unlock automatically.';
  if (code.includes('MANUAL') || code.includes('SECTION'))
    return 'Your program team has locked this section while they work on your application. Contact them if you have questions.';
  return viewOnly
    ? 'This step is view-only right now. Contact your program team if something needs to change.'
    : 'This section is currently locked. Please check back later, or contact your program team if you have questions.';
}

/** `step.<step_key>.status` → `<step_key>`; undefined for any other fact path. */
export function stepKeyFromFact(fact: unknown): string | undefined {
  if (typeof fact !== 'string') return undefined;
  const match = /^step\.(.+)\.status$/.exec(fact.trim());
  return match?.[1] || undefined;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

/** Human-readable fallback when no configured title exists for a step key. */
const humaniseStepKey = (stepKey: string) =>
  stepKey
    .replace(/_v\d+(_\d+)*$/i, '')
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/**
 * Names the prerequisite step from a Globus unlock condition — ONLY for the
 * exact contract shape `{ fact: "step.<key>.status", operator: "equals",
 * value: "approved" }`. Any other operator/value yields undefined so the
 * portal never tells an applicant that some other status unlocks the step.
 * Prefers the configured step title over the raw step key.
 */
export function prerequisiteStepName(
  condition: unknown,
  resolveTitle: StepTitleResolver,
): string | undefined {
  const record = asRecord(condition);
  if (!record) return undefined;
  const key = stepKeyFromFact(record.fact);
  if (!key) return undefined;
  const operator = String(record.operator ?? 'equals').toLowerCase();
  const value = String(record.value ?? '').toLowerCase();
  if (!['equals', 'eq', '=', '=='].includes(operator) || value !== 'approved') return undefined;
  return resolveTitle(key)?.trim() || humaniseStepKey(key);
}

function singleConditionMessage(
  condition: unknown,
  resolveTitle: StepTitleResolver,
): string | undefined {
  const record = asRecord(condition);
  if (!record) return undefined;
  for (const key of ['applicant_message', 'applicantMessage', 'message', 'description']) {
    const text = record[key];
    if (typeof text === 'string' && text.trim()) return text.trim();
  }
  const prerequisite = prerequisiteStepName(record, resolveTitle);
  if (prerequisite)
    return `It opens once our team has approved your ${prerequisite} step — submitting that step is not enough on its own.`;
  // Any other step-status fact: name nothing we can't vouch for.
  if (stepKeyFromFact(record.fact))
    return 'Complete the required earlier application steps before returning here.';
  const type = String(record.type ?? record.kind ?? '').toUpperCase();
  if (type.includes('STAFF') || type.includes('MANUAL'))
    return 'Your program team must unlock this section before you can make changes.';
  if (type.includes('STEP') || type.includes('SEQUENCE') || type.includes('DEPEND'))
    return 'Complete the required earlier application steps before returning here.';
  if (type.includes('TIME') || type.includes('WINDOW'))
    return 'This section will unlock automatically when its access window opens.';
  return undefined;
}

/**
 * Applicant-friendly sentence(s) describing what must happen before the step
 * unlocks. Accepts a single condition object or an array (Globus sends an
 * array). Unknown shapes yield undefined rather than leaking raw JSON.
 */
export function unlockConditionMessage(
  value: unknown,
  resolveTitle: StepTitleResolver = () => undefined,
): string | undefined {
  const conditions = Array.isArray(value) ? value : [value];
  const messages: string[] = [];
  for (const condition of conditions) {
    const text = singleConditionMessage(condition, resolveTitle);
    if (text && !messages.includes(text)) messages.push(text);
  }
  return messages.length ? messages.join(' ') : undefined;
}

/** Full banner text for a locked step lacking a staff-authored message. */
export function lockBannerCopy(input: {
  bannerMessage?: string;
  reason?: string;
  viewOnly?: boolean;
  unlockConditions?: unknown;
  resolveTitle?: StepTitleResolver;
}): { headline: string; condition?: string } {
  const headline = input.bannerMessage ?? defaultLockMessage(input.reason, Boolean(input.viewOnly));
  return {
    headline,
    condition: unlockConditionMessage(input.unlockConditions, input.resolveTitle),
  };
}
